import styled from '@emotion/styled';
import { ChevronLeft, ExternalLink, Save } from 'lucide-react';
import { useFabricEditorOptional } from 'modules/fabric-editor';
import { selectActiveTemplate } from 'modules/manager/store';
import { useSaveTemplate } from 'modules/manager/ui/hooks';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useIsLocal from 'shared/hooks/use-is-local';
import { useAppSelector } from 'store/hooks';
import { buildDevRenderLink } from './dev-render-link';

const EditorHeader: React.FC = () => {
  const navigate = useNavigate();
  const template = useAppSelector(selectActiveTemplate);
  const editor = useFabricEditorOptional();
  const { save, isDirty, isSaving, canSave } = useSaveTemplate(editor);
  const isLocal = useIsLocal();

  const handleBack = useCallback(() => {
    navigate(Pages.MEDIA_MANAGER.INDEX);
  }, [navigate]);

  const devRenderLink = isLocal && template ? buildDevRenderLink(template) : null;

  return (
    <Root>
      <BackButton type="button" onClick={handleBack} aria-label="Назад">
        <ChevronLeft size={18} />
      </BackButton>
      <Title>
        <span className="font-semibold">{template?.name ?? '—'}</span>
        {isDirty && <UnsavedBadge>Несохранённые изменения</UnsavedBadge>}
      </Title>
      <Spacer />
      {devRenderLink && (
        <DevRenderLink
          href={devRenderLink}
          target="_blank"
          rel="noreferrer"
          title={`Только для локальной разработки. Открыть страницу рендера с тестовыми параметрами:\n${devRenderLink}\n\nСтраница читает шаблон из базы — несохранённые изменения в неё не попадут.`}
        >
          <ExternalLink size={16} />
          <span>Рендер</span>
        </DevRenderLink>
      )}
      <SaveButton type="button" onClick={() => void save()} disabled={!canSave} aria-label="Сохранить">
        <Save size={16} />
        <span>{isSaving ? 'Сохранение…' : 'Сохранить'}</span>
      </SaveButton>
    </Root>
  );
};

export default EditorHeader;

const Root = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  height: 56px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: #374151;
  &:hover {
    background: #f3f4f6;
  }
`;

const Title = styled.div`
  font-size: 14px;
  color: #111827;
  display: flex;
  align-items: center;
`;

const UnsavedBadge = styled.span`
  margin-left: 12px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 12px;
  font-weight: 500;
`;

const Spacer = styled.div`
  flex: 1;
`;

/** Local-only — see dev-render-link.ts. Styled as a secondary action so it never competes with Save. */
const DevRenderLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
  &:hover {
    background: #f1f5f9;
  }
`;

const SaveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  background: #2563eb;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;
